import {
  useCallback,
  useContext,
  useEffect,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import KeyboardDoubleArrowDown from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardDoubleArrowRight from "@mui/icons-material/KeyboardDoubleArrowRight";
import {
  Box,
  CircularProgress,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { v4 } from "uuid";

import { dayjs } from "@thalamos/common";
import type {
  ExtendedPatient,
  ExtendedThalamosUser,
  PatientTimelineResponse,
} from "@thalamos/aspire-common";

import { api, apiHooks } from "~/api.js";
import {
  Banner,
  BannerList,
  Button,
  PopupDialog,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";
import { CompactedPatientBanner } from "~/components/layout/PatientBanner/PatientBannerParts.js";
import { LoggedInUserContext } from "~/Contexts.js";
import { useScreenDetection } from "~/hooks/ScreenDetection/useScreenDetection.js";

async function checkForAutoMerges(
  { patient, needsAutoMergeCheck }: PatientTimelineResponse,
  user: ExtendedThalamosUser,
  setTargetPatientId: Dispatch<SetStateAction<string | null>>,
) {
  if (!user.sessionOrganisationConfiguration?.patientAutoMergeEnabled) {
    return;
  }

  if (!patient.nhsNumber) {
    return;
  }

  if (!needsAutoMergeCheck) {
    // If we don't need an auto merge check, we can skip this
    return;
  }

  const searchId = v4();
  const mergeSuggestions = await api.patients.searchV2(searchId, {
    type: "nhs-number",
    nhsNumber: patient.nhsNumber,
    patientSearchVariant: "merge",
    patientIdToExclude: patient.id,
    isAutoMergeCheck: true, // Indicate this is an auto-merge check so we only do it once per session
  });

  if (mergeSuggestions.status === 200) {
    if (mergeSuggestions.data.searchOutcome === "success") {
      const matchedPatient = mergeSuggestions.data.matchedPatients[0];
      if (matchedPatient !== undefined) {
        // Let's get the ID of the duplicate patient
        const searchReasoningResult = await api.patients.addSearchV2Reasoning(
          matchedPatient.resultId,
          {
            reason: "Auto merge",
          },
        );

        if (searchReasoningResult.status === 200) {
          if (
            searchReasoningResult.data.type === "patient-page" ||
            searchReasoningResult.data.type === "s-flag-setup"
          ) {
            setTargetPatientId(searchReasoningResult.data.patientId);
          }
        }
      }
    }
  } else {
    renderErrorToast({
      message: "Failed to check for auto merge suggestions",
    });
  }
}

function AutoMergeConfirmationInner({
  patient1,
  patient2,
  setDialogOpen,
}: {
  patient1: ExtendedPatient;
  patient2: ExtendedPatient;
  setDialogOpen: Dispatch<SetStateAction<boolean>>;
}) {
  const url = useLocation().pathname;
  const navigate = useNavigate();
  // the first patient to be created should be the target patient, and the second one should be the source patient
  const patient1IsOlder = dayjs(patient1.created).isBefore(
    dayjs(patient2.created),
  );
  const targetPatient = patient1IsOlder ? patient1 : patient2;
  const sourcePatient = patient1IsOlder ? patient2 : patient1;

  const { isMobileView } = useScreenDetection();
  const [animateArrow, setAnimateArrow] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation();

  const [merging, setMerging] = useState(false);
  useEffect(() => {
    setAnimateArrow(true);
    return () => setAnimateArrow(false);
  }, []);

  const sourcePatientSensitive =
    sourcePatient.restricted ||
    ["formal", "informal"].includes(
      sourcePatient.deathNotificationStatus ?? "",
    );

  const targetPatientSensitive =
    targetPatient.restricted ||
    ["formal", "informal"].includes(
      targetPatient.deathNotificationStatus ?? "",
    );

  // NHS number match, surname and DoB match, not sensitive
  const canAutoMerge =
    sourcePatient.id !== targetPatient.id &&
    sourcePatient.nhsNumber &&
    targetPatient.nhsNumber &&
    sourcePatient.nhsNumber === targetPatient.nhsNumber &&
    sourcePatient.name.family.toUpperCase() ===
      targetPatient.name.family.toUpperCase() &&
    sourcePatient.dateOfBirth === targetPatient.dateOfBirth &&
    !sourcePatientSensitive &&
    !targetPatientSensitive;

  const mergeFn = useCallback(() => {
    setMerging(true);
    const updatedUrl = url.replace(sourcePatient.id, targetPatient.id);

    void api.patients
      .merge({
        currentPatientId: sourcePatient.id,
        targetPatientId: targetPatient.id,
        reason: "Auto merge based on NHS number match",
        isAutoMerge: true,
      })
      .then((result) => {
        setMerging(false);

        if (result.status === 204) {
          renderSuccessToast({
            message: `Patients with same NHS number have been successfully reconciled`,
          });

          if (updatedUrl !== url) {
            // If the URL has changed, we need to redirect to the new patient page
            navigate(updatedUrl, { replace: true });
          }
          setDialogOpen(false);
        } else {
          renderErrorToast({
            message: "Failed to reconcile patients",
          });
        }
      })
      .catch((_e: unknown) => {
        console.error(_e);
        setMerging(false);
        renderErrorToast({
          message: `Failed to reconcile patients`,
        });
      });
  }, [setDialogOpen, sourcePatient.id, targetPatient.id]);

  useEffect(() => {
    if (canAutoMerge) {
      mergeFn();
    }
  }, [mergeFn, canAutoMerge]);

  return (
    <>
      {canAutoMerge ? (
        "Automatically reconciling patients.."
      ) : (
        <>
          <Banner
            sx={{ mb: "1rem" }}
            bannerType={BannerList.WARNING}
            title="A patient with this NHS number already exists within your instance of eMHA (shown on the right), but with demographics differences. Click confirm to reconcile the patient on the left, including their forms, with the patient on the right"
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              flexDirection: isMobileView ? "column" : "row",
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
              }}
            >
              <Typography
                sx={{
                  fontWeight: "bold",
                  my: 1,
                  ml: 1.875,
                }}
              >
                {t("components.patientMergeDialog.patientRemoved")}
              </Typography>
              <Box sx={{ border: "1px solid #FF3D00", borderRadius: "5px" }}>
                <CompactedPatientBanner
                  patient={sourcePatient}
                  nhsNumber={sourcePatient.nhsNumber ?? undefined}
                  isHidden
                />
              </Box>
            </Box>
            {isMobileView ? (
              <KeyboardDoubleArrowDown
                fontSize="large"
                sx={{
                  mx: 2,
                  opacity: animateArrow ? 1 : 0,
                  transition:
                    "opacity 1s ease-in-out, transform 1s ease-in-out",
                  transform: animateArrow
                    ? "translateX(0)"
                    : "translateX(-50px)",
                }}
              />
            ) : (
              <>
                <KeyboardDoubleArrowRight
                  fontSize="large"
                  sx={{
                    mx: 2,
                    opacity: animateArrow ? 1 : 0,
                    transition:
                      "opacity 1s ease-in-out, transform 1s ease-in-out",
                    transform: animateArrow
                      ? "translateX(0)"
                      : "translateX(-50px)",
                  }}
                />
              </>
            )}

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
              }}
            >
              <Typography
                sx={{
                  fontWeight: "bold",
                  my: 1,
                  ml: 1.875,
                }}
              >
                {t("components.patientMergeDialog.remainPrimaryPatient")}
              </Typography>
              <Box
                sx={{
                  border: `1px solid ${theme.palette.common.limeGreen}`,
                  borderRadius: "5px",
                }}
              >
                <CompactedPatientBanner
                  patient={targetPatient}
                  nhsNumber={targetPatient.nhsNumber ?? undefined}
                  isHidden
                />
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Button
              disabled={merging}
              label="ignore"
              variant="outlined"
              onClick={() => {
                setDialogOpen(false);
              }}
            />
            <Button
              disabled={merging}
              label="confirm"
              onClick={() => {
                mergeFn();
              }}
            />
          </Box>
        </>
      )}
    </>
  );
}

function AutoMergeConfirmation({
  sourcePatient,
  targetPatientId,
}: {
  sourcePatient: ExtendedPatient;
  targetPatientId: string;
}) {
  // Don't subscribe to pusher events in here
  const [
    {
      data: targetPatientTimeline,
      loading: targetPatientTimelineCompleteLoading,
    },
  ] = apiHooks.patients.getTimelineWithoutSubscription(targetPatientId);

  const [dialogOpen, setDialogOpen] = useState(true);

  return (
    <PopupDialog open={dialogOpen} onClose={() => {}}>
      <DialogTitle>Patient Reconciliation</DialogTitle>
      <DialogContent>
        {targetPatientTimelineCompleteLoading || !targetPatientTimeline ? (
          <CircularProgress />
        ) : (
          <AutoMergeConfirmationInner
            setDialogOpen={setDialogOpen}
            patient1={sourcePatient}
            patient2={targetPatientTimeline.patient}
          />
        )}
      </DialogContent>
    </PopupDialog>
  );
}

export function PatientAutoMergeChecker({
  patientTimeline,
}: {
  patientTimeline: PatientTimelineResponse;
}) {
  const userContext = useContext(LoggedInUserContext);
  const user = userContext?.user!;
  const [targetPatientId, setTargetPatientId] = useState<string | null>(null);

  useEffect(() => {
    void checkForAutoMerges(patientTimeline, user, setTargetPatientId);
  }, [patientTimeline, user, setTargetPatientId]);

  return targetPatientId ? (
    <AutoMergeConfirmation
      sourcePatient={patientTimeline.patient}
      targetPatientId={targetPatientId}
    />
  ) : (
    <></>
  );
}
